import { useEffect, useState } from "react";
import { Users, Wrench, ExternalLink, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { BRAND_COLORS, withAlpha } from "../../lib/bookingStatus";

/**
 * Feb 2026 · Verbundene Zuga\u0308nge — Kunden-Detail Sektion
 * (5 Sep 2026 · User-Directive).
 *
 * Zeigt alle mit dem Hauptkunden gekoppelten Familienmitglieder und
 * Partner. Family kommt aus `users.owner_id`; Partner kommen aus
 * `assignments` (distinkte partner_ids fuer diesen customer_id, inklusive
 * der Objekte, an denen sie gearbeitet haben). Zaehler in der
 * Sektions-Ueberschrift bleiben auch bei leeren Listen sichtbar — die
 * Sektion selbst ist so gehalten, dass leere Zustaende freundlich
 * kommuniziert werden.
 */
function Row({ testid, name, email, subtitle, extra }) {
  return (
    <div
      className="flex items-start justify-between gap-4 py-3 border-b border-navy/5 last:border-b-0"
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="text-[14px] text-navy-dark font-medium truncate">{name || email}</div>
        <div className="text-[12px] text-navy-dark/60 truncate">{email}</div>
        {subtitle && <div className="text-[11.5px] text-navy-dark/55 mt-0.5 leading-snug">{subtitle}</div>}
      </div>
      {extra && <div className="shrink-0 label-caps text-[10px]">{extra}</div>}
    </div>
  );
}

export function ConnectedAccessSection({ customerId }) {
  const [state, setState] = useState({ loading: true, family: [], partners: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/team/customers/${customerId}/connected`);
        if (!alive) return;
        setState({ loading: false, family: data.family || [], partners: data.partners || [] });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, family: [], partners: [] });
      }
    })();
    return () => { alive = false; };
  }, [customerId]);

  const totalCount = state.family.length + state.partners.length;

  return (
    <div
      className="bg-white border border-navy/10 p-6 md:p-8 mt-6"
      data-testid="connected-access-section"
    >
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <div className="label-caps mb-2">Verbundene Zug&auml;nge</div>
          <h2 className="font-display text-2xl md:text-3xl text-navy">
            Familie &amp; Partner
          </h2>
        </div>
        <div
          className="label-caps px-2 py-1 shrink-0"
          style={{
            background: withAlpha(BRAND_COLORS.gold, 0.12),
            color: BRAND_COLORS.goldDark,
          }}
          data-testid="connected-access-count"
        >
          {totalCount}
        </div>
      </div>

      {state.loading ? (
        <div className="flex items-center gap-2 text-[13px] text-navy-dark/60 label-caps">
          <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.6} />
          Wird geladen &hellip;
        </div>
      ) : totalCount === 0 ? (
        <p
          className="text-[13px] text-navy-dark/60 italic leading-relaxed"
          data-testid="connected-access-empty"
        >
          Keine Familienmitglieder oder Partner mit diesem Kunden verknüpft.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {/* Family column */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-3.5 h-3.5 text-navy-dark/70" strokeWidth={1.6} />
              <span className="label-caps text-navy-dark/85">
                Familie <span className="text-navy-dark/45">· {state.family.length}</span>
              </span>
            </div>
            {state.family.length === 0 ? (
              <p
                className="text-[12px] text-navy-dark/45 italic"
                data-testid="connected-access-family-empty"
              >
                Keine Familienmitglieder.
              </p>
            ) : (
              <div data-testid="connected-access-family-list">
                {state.family.map((f) => (
                  <Row
                    key={f.id}
                    testid={`connected-family-${f.id}`}
                    name={f.name}
                    email={f.email}
                    subtitle="Zugriff auf alle Objekte des Hauptkunden"
                    extra="Familie"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Partners column */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-3.5 h-3.5 text-navy-dark/70" strokeWidth={1.6} />
              <span className="label-caps text-navy-dark/85">
                Partner <span className="text-navy-dark/45">· {state.partners.length}</span>
              </span>
            </div>
            {state.partners.length === 0 ? (
              <p
                className="text-[12px] text-navy-dark/45 italic"
                data-testid="connected-access-partners-empty"
              >
                Keine Partner gekoppelt.
              </p>
            ) : (
              <div data-testid="connected-access-partners-list">
                {state.partners.map((p) => {
                  const propLabel = p.properties?.length
                    ? p.properties.map((pr) => pr.name).join(" · ")
                    : "Kein Objekt hinterlegt";
                  return (
                    <Row
                      key={p.id}
                      testid={`connected-partner-${p.id}`}
                      name={p.name}
                      email={p.email}
                      subtitle={
                        <span>
                          {propLabel}
                          {typeof p.assignment_count === "number" && (
                            <span className="text-navy-dark/40">
                              {" · "}
                              {p.assignment_count} Auftrag{p.assignment_count === 1 ? "" : "s"}
                            </span>
                          )}
                        </span>
                      }
                      extra="Partner"
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <p className="text-[11.5px] text-navy-dark/50 mt-5 leading-relaxed">
        Verkn&uuml;pfungen sind bidirektional: der Hauptkunde sieht hier
        Familie &amp; Partner, umgekehrt sehen Familie und Partner ihren
        Hauptkunden im Zugangs-Bereich (
        <span className="inline-flex items-center gap-1 label-caps">
          <ExternalLink className="w-3 h-3" strokeWidth={1.6} />
          Zug&auml;nge
        </span>
        ).
      </p>
    </div>
  );
}
